import { Trans } from 'react-i18next';
import './forgotSuccess.css';
import TabletBackButton from '../../component/tabletBackButton/tabletBackButton';
import DesktopBackButton from '../../component/desktopBackButton/desktopBackbutton';
import TabletLayoutImage from '../../component/tabletLayoutImage/tabletLayoutImage';
import DesktopLayoutImage from '../../component/desktopLayoutmage/desktopLayoutImage';
import RenderInPlayLogo from '../../component/inPlayLogo/inPlayLogo';

/**
 * Renders the desktop view for the "Forgot Password Success" screen.
 *
 * This screen is shown after a user has requested a password reset and the system
 * has sent a reset link to their email. It displays a confirmation message and
 * provides a clickable option to return to the login screen.
 *
 * Props:
 * - onClickLogin (function): A callback function to handle navigation back to the login page.
 *
 * UI Elements:
 * - A back link with an arrow icon to return to the login page.
 * - A headline confirming the "Forgot Password" request.
 * - A paragraph stating the reset link has been sent.
 * - An image on the right side representing the login layout.
 */
export const renderDesktopView = (onClickLogin: () => void, email: string) => {
  return (
    <div className="lg:flex h-screen w-full font-helvetica">
      <div className="flex flex-col flex-1 px-10 py-6">
        <DesktopBackButton onClick={onClickLogin} />

        <div className="flex flex-col max-w-[520px] w-full mx-auto">
          <div className="mt-[200px]">
            <RenderInPlayLogo />
            <div className="login-header-container mb-[50px]">
              <h1 className="text-6xl text-[#1A1A1A] mb-6">
                <Trans i18nKey="forgotPassword.forgot" />
                <br />
                <Trans i18nKey="forgotPassword.passwordtext" />
              </h1>
            </div>

            <p className="mb-6 text-[#1A1A1A] text-base">
              <Trans i18nKey="forgotSuccess" /> {email}.
            </p>
          </div>
        </div>
      </div>

      <DesktopLayoutImage />
    </div>
  );
};

/**
 * Renders the tablet view layout for the "Forgot Password" success screen.
 * This includes a full-screen background image with an overlay, a back button,
 * a heading, and a modal-like message indicating that the reset link has been sent.
 *
 * @param handleBackToLogin - Callback function to handle navigation back to the login screen.
 * @param email - The email address to which the password reset link was sent.
 * @returns A JSX element representing the tablet layout.
 */
export const renderTabletView = (
  handleBackToLogin: () => void,
  email: string,
) => {
  return (
    <div className="relative min-h-screen w-full">
      <TabletLayoutImage />

      <div className="absolute inset-0 bg-black/30" />
      <TabletBackButton onClick={handleBackToLogin} />

      <div className="tablet-login-container">
        <div className="flex flex-col">
          <div className="text-white mt-auto mb-20">
            <RenderInPlayLogo className={'mb-10 h-10'} />
            <h1 className="text-4xl sm:text-5xl login-font-style mb-2">
              <Trans i18nKey={'forgotPassword.forgot'} />
              <br />
              <Trans i18nKey={'forgotPassword.passwordtext'} />
            </h1>
          </div>
        </div>

        <div className="flex flex-col justify-center items-center h-full font-helvetica">
          <div className="success-modal-container">
            <div className="success-modal-inner-container">
              <p className="text-[#1A1A1A] text-lg">
                <Trans i18nKey={'forgotPassword.forgotSuccessText'} /> <br />
                {email}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
